import { useCallback, useMemo, useState } from 'react';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { currentProductAdderStatus, productAdderStepState } from '@src/stores/product';
import { useProductSeatmapSelection } from '@src/domains/products/hooks/useProductSeatmapSelection';
import { useGuardedSequentialOpenChange } from '../../hooks/useGuardedSequentialOpenChange';
import { trackStepConfirmed } from '@src/domains/products/features/product-adder-v2/analytics/widgetTracking';
import type { ProductBookingSlot } from '../../types/productBookingSlot';
import type { UseSeatmapSelectorResult } from './types';
import type { EmbedPlanneSeatmapActions } from './EmbedPlanneSeatmap/types';

export function useSeatmapSelector(): UseSeatmapSelectorResult {
  const { i18n } = useTranslate();
  const widgetId = useWidgetId();

  const {
    productId,
    product,
    scheduling,
    requiresSeatSelection,
    selectedHour,
    selectedTariffs,
    selectedResources,
    selectSeatFromMap,
  } = useProductSeatmapSelection();

  const [modalOpen, setModalOpen] = useState(false);
  const { steps } = useAppSelector(productAdderStepState(widgetId));
  const currentStatus = useAppSelector(currentProductAdderStatus(widgetId));
  const isAddingToCart = useMemo(() => currentStatus === 'adding_to_cart', [currentStatus]);

  const isVisible = Boolean(productId && product && requiresSeatSelection && scheduling);
  const sequentialSlot: ProductBookingSlot | null = isVisible && steps?.includes('seat_map') ? 'seat_map' : null;
  const guardedOnModalOpenChange = useGuardedSequentialOpenChange(sequentialSlot, setModalOpen);

  const closeModal = useCallback(() => setModalOpen(false), []);
  const confirmModal = useCallback(() => {
    trackStepConfirmed(widgetId, productId ?? '', 'seat_map');
    setModalOpen(false);
  }, [widgetId, productId]);
  const openModal = useCallback(() => guardedOnModalOpenChange(true), [guardedOnModalOpenChange]);

  const primaryLabel = i18n('adder_goForwardsFooter');

  const embedActions: EmbedPlanneSeatmapActions = useMemo(
    () => ({
      onConfirm: confirmModal,
      onDismiss: closeModal,
      primaryLabel,
    }),
    [confirmModal, closeModal, primaryLabel],
  );

  const selectedResourceLabel = useMemo(() => {
    if (!selectedResources.length) {
      return null;
    }
    return selectedResources.map((r) => r.resourceSlot.name).join(', ');
  }, [selectedResources]);

  return {
    isVisible,
    sequentialSlot,
    disabled: isAddingToCart,
    modalOpen,
    openModal,
    closeModal,
    i18n,
    scheduling,
    selectedHour,
    selectedTariffs,
    selectSeatFromMap,
    embedActions,
    selectedResourceLabel,
  };
}
